import PublicPage from '../../shared/PublicPage/PublicPage';
import { Button } from 'antd';
import Link from 'next/link';
import {
  StyledSubscriptions,
  StyledTitleWithLanguageSelector,
} from './Subscriptions.styled';
import useSubscriptionForTeachers from './useSubscriptionsForTeachers.hook';
import {
  DefaultPricingContainer,
  MostPopular,
  Pricing,
} from './components/Pricing';
import FeaturesList from './components/FeaturesList';
import features from './teacher-features';
import SubscriptionLanguageSelector from './components/SubscriptionLanguageSelector';
import { LightBackgroundPage } from '@/shared/PublicPage/CustomBgPublicPage.styled';

export default function SubscriptionsForTeachers() {
  const { state, actions } = useSubscriptionForTeachers();

  return (
    <PublicPage
      title={
        <StyledTitleWithLanguageSelector>
          <div className="first-row">
            <h1>Teachers and schools plans</h1>
            <Link href="/sign-up/family-subscriptions" passHref>
              <Button>Go to Family plans</Button>
            </Link>
          </div>
          <SubscriptionLanguageSelector
            selectedLanguage={state.selectedLanguage}
            setSelectedLanguage={actions.setSelectedLanguage}
          />
        </StyledTitleWithLanguageSelector>
      }
      StyledPublicPage={LightBackgroundPage}
    >
      <StyledSubscriptions>
        {getSubscriptionTypes({
          language: state.selectedLanguage,
          isTeacherPro: state.isTeacherPro,
          isTeacher: state.isTeacher,
        })?.map((subscriptionType) => (
          <subscriptionType.Component key={subscriptionType.title}>
            <Pricing
              title={subscriptionType.title}
              language={state.selectedLanguage}
              price={subscriptionType.price}
              description={subscriptionType.description}
              Button={subscriptionType.button}
            >
              <FeaturesList data={subscriptionType.features} />
            </Pricing>
          </subscriptionType.Component>
        ))}
      </StyledSubscriptions>
    </PublicPage>
  );
}

function getSubscriptionTypes({ language = 'es', isTeacherPro, isTeacher }) {
  const subscriptionTypes = {
    es: [
      {
        Component: MostPopular,
        title: 'Teacher Pro',
        price: 40,
        description: 'Per month or $299/year',
        button: (
          <>
            <Link href={`/sign-up/buy/1/first`} passHref>
              <Button size="large" type="primary">
                Get Teacher Pro now
              </Button>
            </Link>
            <WantFreeTrialButton
              subscriptionTypeId={1}
              disabled={isTeacherPro}
            />
          </>
        ),
        features: features.map((feature) => ({
          ...feature,
          className: feature.id == 1 ? '' : 'bold',
        })),
      },
      {
        Component: DefaultPricingContainer,
        title: 'Teacher',
        price: 20,
        description: 'Per month or $199/year',
        button: (
          <>
            <Link href={`/sign-up/buy/2/first`} passHref>
              <Button size="large" type="default">
                Get Teacher now
              </Button>
            </Link>
            <WantFreeTrialButton subscriptionTypeId={2} disabled={isTeacher} />
          </>
        ),
        features: features.map((item) => ({
          ...item,
          isDisabled: [features[0].id].indexOf(item.id) === -1,
        })),
      },
    ],
    fr: [
      {
        Component: MostPopular,
        title: 'Teacher Pro',
        price: 19,
        description: 'Per month or $119/year',
        button: (
          <>
            <Link href={`/sign-up/buy/4/first`} passHref>
              <Button size="large" type="primary">
                Get Teacher Pro now
              </Button>
            </Link>
            <WantFreeTrialButton
              subscriptionTypeId={4}
              disabled={isTeacherPro}
            />
          </>
        ),
        features: features.map((feature) => ({
          ...feature,
          className: feature.id == 1 ? '' : 'bold',
        })),
      },
      {
        Component: DefaultPricingContainer,
        title: 'Teacher',
        price: 14,
        description: 'Per month or $79/year',
        button: (
          <>
            <Link href={`/sign-up/buy/5/first`} passHref>
              <Button size="large" type="default">
                Get Teacher now
              </Button>
            </Link>
            <WantFreeTrialButton subscriptionTypeId={5} disabled={isTeacher} />
          </>
        ),
        features: features.map((item) => ({
          ...item,
          isDisabled: [features[0].id].indexOf(item.id) === -1,
        })),
      },
    ],
    all: [
      {
        Component: MostPopular,
        title: 'Teacher Pro',
        price: 59,
        description: 'Per month or $399/year',
        button: (
          <>
            <Link href={`/sign-up/buy/7/first`} passHref>
              <Button size="large" type="primary">
                Get Teacher Pro now
              </Button>
            </Link>
            <WantFreeTrialButton
              subscriptionTypeId={7}
              disabled={isTeacherPro}
            />
          </>
        ),
        features: features.map((feature) => ({
          ...feature,
          className: feature.id == 1 ? '' : 'bold',
        })),
      },
      {
        Component: DefaultPricingContainer,
        title: 'Teacher',
        price: 29,
        description: 'Per month or $249/year',
        button: (
          <>
            <Link href={`/sign-up/buy/8/first`} passHref>
              <Button size="large" type="default">
                Get Teacher now
              </Button>
            </Link>
            <WantFreeTrialButton subscriptionTypeId={8} disabled={isTeacher} />
          </>
        ),
        features: features.map((item) => ({
          ...item,
          isDisabled: [features[0].id].indexOf(item.id) === -1,
        })),
      },
    ],
  };

  return subscriptionTypes[language];
}

function WantFreeTrialButton({ subscriptionTypeId, disabled }) {
  const button = (
    <Button size="large" type="default" disabled={disabled}>
      Start free trial
    </Button>
  );
  return disabled ? (
    button
  ) : (
    <Link href={`/sign-up/try/${subscriptionTypeId}/first`} passHref>
      {button}
    </Link>
  );
}
